import React, { useEffect, useRef, useState } from 'react';

const MENU_GROUPS = [
  {
    key: 'solutions',
    label: 'Solutions',
    items: ['Direct Mail API', 'Storefront Solution', 'Print Products', 'CRM Integration']
  },
  {
    key: 'use-cases',
    label: 'Use Cases',
    items: ['CRMs', 'Real Estate', 'Franchises', 'Sales Follow-Up']
  },
  { key: 'pricing', label: 'Pricing' },
  { key: 'contact', label: 'Contact' }
];

function SiteHeader({
  isDark,
  isAuthenticated,
  user,
  onToggleTheme,
  onOpenAuth,
  onOpenLanding,
  onOpenDashboard,
  onLogout,
  onMenuAction
}) {
  const [openMenuKey, setOpenMenuKey] = useState('');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const rootRef = useRef(null);
  const menuCloseTimerRef = useRef(null);
  const userCloseTimerRef = useRef(null);

  const clearMenuCloseTimer = () => {
    if (menuCloseTimerRef.current) {
      window.clearTimeout(menuCloseTimerRef.current);
      menuCloseTimerRef.current = null;
    }
  };

  const clearUserCloseTimer = () => {
    if (userCloseTimerRef.current) {
      window.clearTimeout(userCloseTimerRef.current);
      userCloseTimerRef.current = null;
    }
  };

  const openMenuByHover = (menuKey) => {
    clearMenuCloseTimer();
    setOpenMenuKey(menuKey);
  };

  const closeMenuByHover = () => {
    clearMenuCloseTimer();
    menuCloseTimerRef.current = window.setTimeout(() => {
      setOpenMenuKey('');
    }, 140);
  };

  const openUserByHover = () => {
    clearUserCloseTimer();
    setIsUserMenuOpen(true);
  };

  const closeUserByHover = () => {
    clearUserCloseTimer();
    userCloseTimerRef.current = window.setTimeout(() => {
      setIsUserMenuOpen(false);
    }, 140);
  };

  useEffect(() => {
    const onDocumentClick = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpenMenuKey('');
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', onDocumentClick);

    return () => {
      document.removeEventListener('mousedown', onDocumentClick);
      clearMenuCloseTimer();
      clearUserCloseTimer();
    };
  }, []);

  const onSelectMenu = (menuKey) => {
    setOpenMenuKey('');
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
    if (onMenuAction) onMenuAction(menuKey);
  };

  const onSelectSubItem = (menuKey) => {
    setOpenMenuKey('');
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
    if (onMenuAction) onMenuAction(menuKey);
  };

  const onLogoClick = () => {
    if (isAuthenticated) {
      onOpenDashboard();
      return;
    }
    onOpenLanding();
  };

  const userInitial = (user?.name || user?.email || 'U').trim().charAt(0).toUpperCase();

  return (
    <header
      ref={rootRef}
      className={`relative rounded-2xl border px-4 py-3 backdrop-blur-xl transition md:px-5 ${
        isDark
          ? 'border-slate-700 bg-slate-900/85 shadow-[0_10px_30px_-15px_rgba(0,0,0,0.8)]'
          : 'border-white/80 bg-white/90 shadow-[0_10px_30px_-15px_rgba(15,23,42,0.25)]'
      }`}
    >
      <div
        className={`mb-2 hidden items-center justify-between gap-3 border-b pb-2 text-xs lg:flex ${
          isDark ? 'border-slate-700' : 'border-slate-200'
        }`}
      >
        <div className="flex items-center gap-3">
          <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>info@postcardsaas.com</span>
          <span className={isDark ? 'text-slate-600' : 'text-slate-300'}>|</span>
          <span className={isDark ? 'text-slate-300' : 'text-slate-700'}>1-800-690-0945</span>
        </div>
        <span
          className={`rounded-full px-3 py-1 font-semibold ${
            isDark ? 'bg-slate-800 text-cyan-300' : 'bg-slate-100 text-cyan-700'
          }`}
        >
          USA Direct Mail Automation
        </span>
      </div>

      <div className="flex items-center justify-between gap-3">
        <button type="button" onClick={onLogoClick} className="group inline-flex items-center gap-2" aria-label="Go to home">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 via-blue-500 to-orange-400 text-sm font-black text-white shadow-lg transition duration-300 group-hover:scale-105 group-hover:rotate-3">
            PS
          </span>
          <span>
            <span className="block text-sm font-black tracking-wide">Postcard SaaS</span>
            <span
              className={`block text-[11px] transition ${
                isDark ? 'text-slate-400 group-hover:text-slate-200' : 'text-slate-500 group-hover:text-slate-700'
              }`}
            >
              AI Direct Mail Platform
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-2 lg:flex" aria-label="Main menu">
          {MENU_GROUPS.map((group) => (
            <div
              key={group.key}
              className="relative"
              onMouseEnter={group.items ? () => openMenuByHover(group.key) : undefined}
              onMouseLeave={group.items ? closeMenuByHover : undefined}
            >
              <button
                type="button"
                onClick={() => {
                  if (group.items) {
                    setOpenMenuKey((prev) => (prev === group.key ? '' : group.key));
                  } else {
                    onSelectMenu(group.key);
                  }
                }}
                className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${
                  isDark ? 'text-slate-200 hover:bg-slate-800 hover:text-cyan-300' : 'text-slate-700 hover:bg-slate-100 hover:text-cyan-700'
                }`}
              >
                {group.label}
              </button>

              {group.items && openMenuKey === group.key && (
                <div className="absolute right-0 top-full z-30 pt-1">
                  <div
                    className={`w-56 rounded-xl border p-2 shadow-xl ${
                      isDark ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white'
                    }`}
                  >
                    {group.items.map((item) => (
                      <button
                        key={item}
                        type="button"
                        onClick={() => onSelectSubItem(group.key)}
                        className={`block w-full rounded-lg px-3 py-2 text-left text-sm transition ${
                          isDark ? 'text-slate-200 hover:bg-slate-800 hover:text-cyan-300' : 'text-slate-700 hover:bg-slate-100 hover:text-cyan-700'
                        }`}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onToggleTheme}
            className={`rounded-lg px-3 py-2 text-xs font-semibold transition md:text-sm ${
              isDark ? 'bg-slate-800 text-slate-100 hover:bg-slate-700' : 'bg-slate-900 text-white hover:bg-slate-700'
            }`}
          >
            {isDark ? 'Light' : 'Dark'}
          </button>

          <div className="relative" onMouseEnter={openUserByHover} onMouseLeave={closeUserByHover}>
            <button
              type="button"
              onClick={() => setIsUserMenuOpen((prev) => !prev)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 via-cyan-400 to-orange-400 text-sm font-black text-white shadow transition hover:scale-105"
              aria-label="User menu"
            >
              {userInitial || 'U'}
            </button>

            {isUserMenuOpen && (
              <div className="absolute right-0 top-full z-40 pt-1">
                <div
                  className={`w-48 rounded-xl border p-2 shadow-2xl ${
                    isDark ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white'
                  }`}
                >
                  {isAuthenticated ? (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          onOpenDashboard();
                          setIsUserMenuOpen(false);
                        }}
                        className={`block w-full rounded-lg px-3 py-2 text-left text-sm font-semibold transition ${
                          isDark ? 'text-slate-200 hover:bg-slate-800 hover:text-cyan-300' : 'text-slate-700 hover:bg-slate-100 hover:text-cyan-700'
                        }`}
                      >
                        Dashboard
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          onLogout();
                          setIsUserMenuOpen(false);
                        }}
                        className="block w-full rounded-lg px-3 py-2 text-left text-sm font-semibold text-rose-600 transition hover:bg-rose-50"
                      >
                        Logout
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          onOpenAuth('login');
                          setIsUserMenuOpen(false);
                        }}
                        className={`block w-full rounded-lg px-3 py-2 text-left text-sm font-semibold transition ${
                          isDark ? 'text-slate-200 hover:bg-slate-800 hover:text-cyan-300' : 'text-slate-700 hover:bg-slate-100 hover:text-cyan-700'
                        }`}
                      >
                        Login
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          onOpenAuth('register');
                          setIsUserMenuOpen(false);
                        }}
                        className="block w-full rounded-lg px-3 py-2 text-left text-sm font-semibold text-blue-600 transition hover:bg-blue-50"
                      >
                        Register
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            className={`rounded-lg px-3 py-2 text-xs font-semibold transition lg:hidden ${
              isDark ? 'bg-slate-800 text-slate-100' : 'bg-slate-100 text-slate-700'
            }`}
          >
            Menu
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div
          className={`mt-3 grid gap-1 rounded-xl border p-2 lg:hidden ${
            isDark ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white'
          }`}
        >
          {MENU_GROUPS.map((group) => (
            <button
              key={group.key}
              type="button"
              onClick={() => onSelectMenu(group.key)}
              className={`rounded-lg px-3 py-2 text-left text-sm font-semibold ${
                isDark ? 'text-slate-200 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-100'
              }`}
            >
              {group.label}
            </button>
          ))}
        </div>
      )}
    </header>
  );
}

export default SiteHeader;

